import { useParams } from "react-router-dom";
import useBackend from "./use_backend";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../enums/react_query_key_enum";
import Snackbar from "../scripts/snackbar";
import { addOwnership, deactivateSite, fetchSiteData, renameSite, replaceBox } from "../scripts/api_v2/site_client";
import { fetchSupportedDevices } from "../scripts/api_v2/device_client";

export function useSiteData() {
  const backend = useBackend();
  const params = useParams();

  return useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: true, // sitePage will refresh the query
  });
}

export function useDevices() {
  const siteData = useSiteData();
  return siteData.data?.devices;
}

export function useDevice(deviceId) {
  const devices = useDevices();
  return devices && deviceId && devices[deviceId];
}

export function useSupportedDevicesData() {
  const backend = useBackend();

  return useQuery({
    queryKey: [REACT_QUERY_KEYS.SUPPORTED_DEVICES, backend.fetch],
    queryFn: () => fetchSupportedDevices(backend.fetch),
    enabled: true, // sitePage will refresh the query
  });
}

export function useRenameSiteMutation({ onSuccess }) {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => await renameSite(backend.fetch, data.siteId, data.siteName),
    onSuccess: (newData, variables) => {
      Snackbar.success("Renaming site success");

      queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, String(variables.siteId)], (oldData) => {
        if (!oldData) return oldData; // Ensure old data exists else return nothing
        return { ...oldData, site_name: variables.siteName };
      });
      if (onSuccess) {
        onSuccess(newData, variables);
      }
    },
    onError: (err, data, context) => {
      Snackbar.error("Renaming site failed: " + err);
    },
  });
}

export function useAddOwnerMutation({ onSuccess }) {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => await addOwnership(backend.fetch, data.siteId, data.partnerCompanyId),
    onSuccess: (newData, variables) => {
      queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, String(variables.siteId)], (oldData) => {
        if (!oldData) return oldData; // Ensure old data exists else return nothing
        const currentOwners = oldData.owners + ", " + variables.partnerCompanyName;
        return { ...oldData, owners: currentOwners };
      });

      Snackbar.success("Owner was added");
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (err, data, context) => {
      Snackbar.error("Adding owner failed: " + err);
    },
  });
}

export function useReplaceSmartboxMutation({ onSuccess }) {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => await replaceBox(backend.fetch, data.siteId, data.boxId),
    onSuccess: (newData, variables) => {
      queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, String(variables.siteId)], (oldData) => {
        if (!oldData) return oldData; // Ensure old data exists else return nothing
        return { ...oldData, box_id: variables.boxId };
      });

      Snackbar.success("Box replacement success");
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (err, data, context) => {
      Snackbar.error("Box replacement failed: " + err);
    },
  });
}

export function useDeactivateSiteMutation({ onSuccess }) {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) =>
      await deactivateSite(
        backend.fetch,
        data.siteId,
        data.username ?? backend.login?.username ?? backend.login?.name ?? "",
        data.reason ?? ""
      ),
    onSuccess: (_, variables) => {
      queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, String(variables.siteId)], (oldData) => {
        if (!oldData) return oldData;
        return { ...oldData, is_active: false };
      });

      Snackbar.success("Site deactivated");
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (err, data, context) => {
      Snackbar.error("Deactivating site failed: " + err);
    },
  });
}
